import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  Link,
  InputAdornment,
  IconButton
} from '@mui/material';
import {
  Person as PersonIcon,
  Lock as LockIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  ArrowForward as ArrowForwardIcon,
  Shield as ShieldIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';

// Design tokens
const PRIMARY = '#4b2bee';
const SURFACE = '#f6f6f8';
const ON_SURFACE = '#0f172a';
const ON_SURFACE_VARIANT = '#475569';
const OUTLINE_VARIANT = '#e2e8f0';

interface LoginProps {
  onRequiresPasswordReset?: () => void;
  onForgotPassword?: () => void;
  onAdminLogin?: () => void;
}

const Login: React.FC<LoginProps> = ({ onRequiresPasswordReset, onForgotPassword, onAdminLogin }) => {
  const [loginId, setLoginId] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const { login } = useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    const result = await login(loginId, password);

    if (!result.success) {
      setError(result.error || 'Login failed');
      setLoading(false);
    } else if (result.requiresPasswordReset) {
      if (onRequiresPasswordReset) onRequiresPasswordReset();
      setLoading(false);
    } else {
      setLoading(false);
    }
  };

  const handleForgotPassword = (e: React.MouseEvent) => {
    e.preventDefault();
    if (onForgotPassword) onForgotPassword();
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: SURFACE,
        px: 2,
      }}
    >
      {/* Logo & Title */}
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <Box
          sx={{
            width: 56,
            height: 56,
            borderRadius: 2,
            bgcolor: PRIMARY,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mx: 'auto',
            mb: 2,
          }}
        >
          <Typography sx={{ color: '#fff', fontSize: 24 }}>✦</Typography>
        </Box>
        <Typography sx={{ fontFamily: 'Manrope, sans-serif', fontWeight: 800, fontSize: '1.75rem', color: ON_SURFACE, mb: 0.5 }}>
          ConnectPie SMS
        </Typography>
        <Typography sx={{ fontFamily: 'Inter, sans-serif', color: ON_SURFACE_VARIANT, letterSpacing: 2, textTransform: 'uppercase', fontSize: '0.6875rem' }}>
          SMS Platform
        </Typography>
      </Box>

      {/* Login Card */}
      <Box
        sx={{
          width: '100%',
          maxWidth: 420,
          bgcolor: '#ffffff',
          borderRadius: 3,
          p: 4,
          boxShadow: '0px 12px 32px rgba(15, 23, 42, 0.04)',
        }}
      >
        {error && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
            {error}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit}>
          {/* Login ID */}
          <Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, color: ON_SURFACE_VARIANT, textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.6875rem', mb: 0.8 }}>
            Login ID
          </Typography>
          <TextField
            fullWidth
            value={loginId}
            onChange={(e) => setLoginId(e.target.value)}
            placeholder="+1234567890"
            required
            autoFocus
            sx={{
              mb: 2.5,
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                bgcolor: '#ffffff',
                '& fieldset': { borderColor: `rgba(226,232,240,0.5)` },
                '&:hover fieldset': { borderColor: OUTLINE_VARIANT },
                '&.Mui-focused fieldset': { borderColor: PRIMARY, boxShadow: `0 0 0 3px rgba(75,43,238,0.1)` },
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PersonIcon sx={{ color: '#94a3b8', fontSize: 20 }} />
                </InputAdornment>
              ),
            }}
          />

          {/* Password */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.8 }}>
            <Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, color: ON_SURFACE_VARIANT, textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.6875rem' }}>
              Password
            </Typography>
            <Link
              component="button"
              variant="caption"
              onClick={handleForgotPassword}
              type="button"
              sx={{ color: PRIMARY, textDecoration: 'none', fontWeight: 500, fontFamily: 'Inter, sans-serif', '&:hover': { textDecoration: 'underline' } }}
            >
              Forgot?
            </Link>
          </Box>
          <TextField
            fullWidth
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="••••••••"
            required
            sx={{
              mb: 3,
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                bgcolor: '#ffffff',
                '& fieldset': { borderColor: `rgba(226,232,240,0.5)` },
                '&:hover fieldset': { borderColor: OUTLINE_VARIANT },
                '&.Mui-focused fieldset': { borderColor: PRIMARY, boxShadow: `0 0 0 3px rgba(75,43,238,0.1)` },
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockIcon sx={{ color: '#94a3b8', fontSize: 20 }} />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                    size="small"
                    sx={{ color: '#94a3b8' }}
                  >
                    {showPassword ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          {/* Sign In Button */}
          <Button
            type="submit"
            fullWidth
            variant="contained"
            disabled={loading}
            endIcon={!loading && <ArrowForwardIcon />}
            sx={{
              bgcolor: PRIMARY,
              borderRadius: 2,
              py: 1.5,
              textTransform: 'none',
              fontFamily: 'Inter, sans-serif',
              fontWeight: 600,
              fontSize: '0.9375rem',
              boxShadow: '0 4px 12px rgba(75,43,238,0.25)',
              '&:hover': { bgcolor: '#3d1fd4', boxShadow: '0 6px 16px rgba(75,43,238,0.35)' },
              '&.Mui-disabled': { bgcolor: OUTLINE_VARIANT },
            }}
          >
            {loading ? 'Signing In...' : 'Sign In'}
          </Button>
        </Box>

        {/* Request Access */}
        <Typography sx={{ textAlign: 'center', mt: 3, fontFamily: 'Inter, sans-serif', color: ON_SURFACE_VARIANT, fontSize: '0.875rem' }}>
          New to ConnectPie?{' '}
          <Link href="#" sx={{ color: PRIMARY, fontWeight: 500, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
            Request Access
          </Link>
        </Typography>
      </Box>

      {/* Footer */}
      <Box sx={{ mt: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#00b894' }} />
          <Typography sx={{ fontFamily: 'Inter, sans-serif', color: ON_SURFACE_VARIANT, letterSpacing: 1, textTransform: 'uppercase', fontSize: '0.6875rem' }}>
            System Operational
          </Typography>
        </Box>
        <Typography sx={{ color: OUTLINE_VARIANT }}>|</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <ShieldIcon sx={{ fontSize: 14, color: ON_SURFACE_VARIANT }} />
          <Typography sx={{ fontFamily: 'Inter, sans-serif', color: ON_SURFACE_VARIANT, letterSpacing: 1, textTransform: 'uppercase', fontSize: '0.6875rem' }}>
            V2.4.0 Secure
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default Login;
